import { isHiddenGame } from "@bing/shared";
import { useState, type ReactNode } from "react";
import bluffPhoto from "../assets/games/bluff.jpg";
// A game with a square of its own uses it (`assets/guides/`), not the lobby tile: the row renders
// a 48×48 `object-cover` box, so a 4:3 tile loses both sides, and these are framed for that crop
// instead. That was the four BOARD games until 2026-09-03; حکم and بی‌دل joined them when their
// masters arrived, شلم when its master did (2026-10-22, `tools/card-art/cut.cjs`); پاسور's is cut
// from its 1376×768 banner (2026-10-10, no master exists — `tools/card-art/guide-from-banner.cjs`).
// بلوف still falls back to the lobby tile — see `assets/game-art.ts`.
import backgammonGuidePhoto from "../assets/guides/backgammon.jpg";
import chessGuidePhoto from "../assets/guides/chess.jpg";
import doozGuidePhoto from "../assets/guides/dooz.jpg";
import heartsGuidePhoto from "../assets/guides/hearts.jpg";
import hokmGuidePhoto from "../assets/guides/hokm.jpg";
import laddersGuidePhoto from "../assets/guides/ladders.jpg";
import menschGuidePhoto from "../assets/guides/mensch.jpg";
import pasurGuidePhoto from "../assets/guides/pasur.jpg";
import shelemGuidePhoto from "../assets/guides/shelem.jpg";
import { GUIDE_ART_WEBP } from "../assets/game-art";
import { ArtPicture } from "../components/ArtPicture";
import { GameIcon } from "../components/GameIcon";
import { PageShell } from "../components/PageShell";
import {
  GAME_GUIDES,
  GUIDE_SECTION_TITLES,
  type GameGuide,
  type GuideGameId,
} from "../content/guides";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/** Bundled game photos, keyed by game id: a purpose-framed 360×360 square where one has been cut
 *  for the game, else the 4:3 lobby tile. */
const PHOTOS: Readonly<Record<string, string>> = {
  hokm: hokmGuidePhoto,
  shelem: shelemGuidePhoto,
  pasur: pasurGuidePhoto,
  hearts: heartsGuidePhoto,
  bluff: bluffPhoto,
  chess: chessGuidePhoto,
  mensch: menschGuidePhoto,
  ladders: laddersGuidePhoto,
  backgammon: backgammonGuidePhoto,
  dooz: doozGuidePhoto,
};

/**
 * Guides (راهنما) — every game's full rules as an RTL accordion. The guide
 * texts live in `content/guides.ts` (engine-faithful, long-form Persian). All
 * rows start collapsed; tapping a header toggles it, and any number of guides
 * may be open at once.
 */

/** A section heading + its content, shared by all five blocks of a guide. */
function GuideSection({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="flex flex-col gap-xs">
      <h3 className="m-0 font-label-md text-label-md text-secondary">{title}</h3>
      {children}
    </section>
  );
}

/** A short single-paragraph section body. */
function GuideParagraph({ text }: { text: string }): ReactNode {
  return (
    <p className="m-0 font-body-md text-body-md leading-relaxed text-on-surface-variant">{text}</p>
  );
}

/** The numbered روش بازی steps (Persian digits, gold markers). */
function GuideSteps({ steps }: { steps: readonly string[] }): ReactNode {
  return (
    <ol className="m-0 flex list-none flex-col gap-xs p-0">
      {steps.map((step, i) => (
        <li key={step} className="flex items-start gap-sm">
          <span
            aria-hidden="true"
            className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-felt-deep font-caption text-caption text-gold-deep"
          >
            {faDigits(i + 1)}
          </span>
          <span className="min-w-0 flex-1 font-body-md text-body-md leading-relaxed text-on-surface-variant">
            {step}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** A bulleted list section body (قوانین کلیدی / امتیازشماری). */
function GuideBullets({ items }: { items: readonly string[] }): ReactNode {
  return (
    <ul className="m-0 flex list-none flex-col gap-xs p-0">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-sm">
          <span
            aria-hidden="true"
            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-deep/70"
          />
          <span className="min-w-0 flex-1 font-body-md text-body-md leading-relaxed text-on-surface-variant">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One expandable game guide (accordion row). */
function GuideAccordion({
  guide,
  open,
  onToggle,
}: {
  guide: GameGuide;
  open: boolean;
  onToggle: () => void;
}): ReactNode {
  const bodyId = `guide-body-${guide.id}`;
  return (
    <section className="overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-low/80 shadow-lg backdrop-blur-md">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={guide.name}
        onClick={onToggle}
        className="flex w-full appearance-none cursor-pointer items-center gap-sm border-0 bg-transparent p-md text-right transition-colors hover:bg-surface-variant/30"
      >
        <span
          aria-hidden="true"
          className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gold-deep/25 bg-felt-deep"
        >
          {PHOTOS[guide.id] ? (
            <ArtPicture
              webp={GUIDE_ART_WEBP[guide.id]}
              src={PHOTOS[guide.id]}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-headline-sm text-headline-sm text-gold-deep">
            {guide.name}
          </span>
          <span className="block font-caption text-caption text-on-surface-variant">
            {guide.summary}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="shrink-0 text-on-surface-variant transition-transform duration-300"
          style={{ transform: open ? "rotate(90deg)" : "rotate(-90deg)" }}
        >
          <GameIcon type="back" size={18} />
        </span>
      </button>
      {open ? (
        <div
          id={bodyId}
          className="flex flex-col gap-md border-t border-outline-variant/20 p-md"
          // `bg-slidein` is the shared reveal keyframe from styles/theme.css.
          style={{ animation: "bg-slidein 0.25s ease-out" }}
        >
          <GuideSection title={GUIDE_SECTION_TITLES.goal}>
            <GuideParagraph text={guide.goal} />
          </GuideSection>
          <GuideSection title={GUIDE_SECTION_TITLES.players}>
            <GuideParagraph text={guide.players} />
          </GuideSection>
          <GuideSection title={GUIDE_SECTION_TITLES.howToPlay}>
            <GuideSteps steps={guide.howToPlay} />
          </GuideSection>
          <GuideSection title={GUIDE_SECTION_TITLES.keyRules}>
            <GuideBullets items={guide.keyRules} />
          </GuideSection>
          <GuideSection title={GUIDE_SECTION_TITLES.scoring}>
            <GuideBullets items={guide.scoring} />
          </GuideSection>
        </div>
      ) : null}
    </section>
  );
}

export function Guides(): ReactNode {
  // ITEM 13 — back goes where the user CAME FROM, like Settings and History. Falling
  // through to PageShell's hardcoded lobby is right only while the drawer is the sole
  // way in; the moment anything else opens this screen it is silently wrong.
  const setView = useSession((st) => st.setView);
  const pageReturn = useSession((st) => st.pageReturn);
  // Multi-open accordion: every guide starts COLLAPSED; only user-opened ones show.
  const [openIds, setOpenIds] = useState<ReadonlySet<GuideGameId>>(new Set());

  const toggle = (id: GuideGameId): void => {
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <PageShell title={fa.guides.title} icon="help" onBack={() => setView(pageReturn)}>
      <p className="m-0 -mt-sm px-sm font-body-md text-body-md text-on-surface-variant">
        {fa.guides.intro}
      </p>
      <div className="flex flex-col gap-md">
        {/* A game hidden from players (`HIDDEN_GAMES` — بلوف, operator 2026-10-03) keeps its guide
            in `content/guides.ts` and is never rendered. */}
        {GAME_GUIDES.filter((g) => !isHiddenGame(g.id)).map((g) => (
          <GuideAccordion
            key={g.id}
            guide={g}
            open={openIds.has(g.id)}
            onToggle={() => toggle(g.id)}
          />
        ))}
      </div>
    </PageShell>
  );
}
